<template>
  <ToDoEmptyState v-if="showEmptyState" />
  <v-sheet v-else class="px-6 py-4" elevation="2" rounded>
    <div class="d-flex align-center justify-space-between mb-2">
      <p
        data-testid="home-text-toDoTitle"
        class="ma-0 pa-0 subtitle-2 blue-grey--text text--darken-3 font-weight-700"
      >
        {{ $t('home.to_do.your_pending_work') }}
      </p>
      <Button
        plain
        color="primary"
        size="small"
        data-testid="home-text-toDoTitle"
        :text="$t('home.view_all')"
        :to="exploreToDoRouteByEntity"
        @click="trackHomeNavigate({ section: 'toDo', element: 'viewAllToDo' })"
      />
    </div>

    <EntityCountsSkeletons v-if="loadingTotalItems" />
    <EntityCounts
      v-else
      :activeEntity="activeEntity"
      :totals="activeEntities"
      @click="setActiveEntity"
    />

    <template v-if="loading || loadingTotalItems">
      <div class="d-flex mt-4">
        <div
          class="todo-item"
          v-for="item in 2"
          :key="item"
          :class="{ 'me-2': item === 1 }"
        >
          <ToDoCardSkeleton
            :showImage="activeEntity !== 'task' && activeEntity !== 'routine'"
          />
        </div>
      </div>
    </template>
    <Carousel v-else height="190" class="mt-6">
      <!-- On small screens we should generate the carousel's pages using individual cards instead of chunks of 2 elements. -->
      <template v-if="$vuetify.breakpoint.xs">
        <v-carousel-item
          v-for="(item, index) in toDoItems"
          :key="`todoPage-${index}`"
        >
          <ToDoItemCard style="height: 160px;" :item="item" :key="item.id" />
        </v-carousel-item>
      </template>

      <!-- On big screens we should use chunks of 2 cards to generate the carousel's pages -->
      <template v-else>
        <v-carousel-item
          v-for="(page, index) in itemPages"
          :key="`todoPage-${index}`"
        >
          <div class="d-flex">
            <div
              v-for="(item, itemIndex) in page"
              class="todo-item"
              style="height: 160px; max-width: 50%;"
              :class="{ 'me-2': itemIndex === 0 }"
              :key="item.id"
            >
              <ToDoItemCard style="height: 100%;" :item="item" />
            </div>
          </div>
        </v-carousel-item>
      </template>
    </Carousel>

    <FindingDrawer
      v-if="isFindingDrawerOpen"
      :visible="isFindingDrawerOpen"
      :findingId="selectedFindingId"
      @close="handleFindingDrawerClose"
      @deleted="handleFindingDeleted"
      @edited="fetchToDoItems"
    />
  </v-sheet>
</template>

<script>
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'
import _ from 'lodash'

import Button from '@/components/UI/Button.vue'
import FindingDrawer from '@/components/FindingDrawer/FindingDrawer.vue'

import ToDoCardSkeleton from './ToDoCardSkeleton.vue'
import ToDoItemCard from './ToDoItemCard.vue'
import ToDoEmptyState from './ToDoEmptyState.vue'
import Carousel from '@/components/UI/Carousel.vue'
import EntityCounts from '@/containers/HomeView/ToDo/EntityCounts/EntityCounts.vue'
import EntityCountsSkeletons from '@/containers/HomeView/ToDo/EntityCounts/EntityCountsSkeletons.vue'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import { HOME_WIDGET_FETCH_DELAYS } from '../constants'

const MAX_FEATURED_ITEMS = 4

const TODO_ENTITY_IDS = Object.freeze({
  REV: 'rev',
  IMPROVEMENT_IDEA: 'improvementIdea',
  QUICK_ACTION: 'quickAction',
  PROBLEM_SOLVING: 'problemSolving',
  TASK: 'task',
  ROUTINE: 'routine',
  FINDING: 'finding',
})

const buildTotals = ({
  totalRevs = 0,
  totalImprovementIdeas = 0,
  totalQuickActions = 0,
  totalProblemSolvings = 0,
  totalTasks = 0,
  totalRoutines = 0,
  totalFindings = 0,
}) => [
  { id: TODO_ENTITY_IDS.REV, value: totalRevs },
  { id: TODO_ENTITY_IDS.IMPROVEMENT_IDEA, value: totalImprovementIdeas },
  { id: TODO_ENTITY_IDS.QUICK_ACTION, value: totalQuickActions },
  { id: TODO_ENTITY_IDS.PROBLEM_SOLVING, value: totalProblemSolvings },
  { id: TODO_ENTITY_IDS.TASK, value: totalTasks },
  { id: TODO_ENTITY_IDS.ROUTINE, value: totalRoutines },
  { id: TODO_ENTITY_IDS.FINDING, value: totalFindings },
]

const DEFAULT_EXPANSIONS = [
  'assignees',
  'image',
  'createdBy',
  'tags',
  'owner',
  'coach',
  'collaborators',
  'organization',
  'site',
  'routineDefinition',
]

export default {
  name: 'ToDo',

  components: {
    Button,
    FindingDrawer,
    ToDoCardSkeleton,
    ToDoItemCard,
    Carousel,
    EntityCounts,
    EntityCountsSkeletons,
    ToDoEmptyState,
  },

  mixins: [mixin, trackingMixin],

  data() {
    return {
      activeEntity: null,
      deferredLoadTimer: null,
      isFindingDrawerOpen: false,
      isRefreshQueued: false,
      isRevBreakdownPending: false,
      isWidgetRoundInFlight: false,
      isShowingCachedSnapshot: false,
      isTotalsComplete: false,
      loading: true,
      loadingTotalItems: true,
      pendingAuxTotals: null,
      selectedFindingId: null,
      MAX_FEATURED_ITEMS,
      showCreateModal: false,
      toDoItems: [],
      itemPages: [],
      totals: [],
    }
  },

  mounted() {
    this.hydrateFromSnapshot()
    this.deferredLoadTimer = setTimeout(this.loadToDoData, HOME_WIDGET_FETCH_DELAYS.toDo)

    eventHub.$on(HUB_EVENTS.ENTITY_DRAWER.DELETED, this.deleteItem)

    eventHub.$on(HUB_EVENTS.TASK_EDITION_DRAWER.DELETED, this.deleteItem)

    eventHub.$on(HUB_EVENTS.ENTITY_DRAWER.UPDATED, this.refreshWidget)

    eventHub.$on(HUB_EVENTS.TASK_EDITION_DRAWER.COMPLETED, this.refreshWidget)
  },

  beforeDestroy() {
    clearTimeout(this.deferredLoadTimer)

    eventHub.$off(HUB_EVENTS.ENTITY_DRAWER.DELETED, this.deleteItem)
    eventHub.$off(HUB_EVENTS.TASK_EDITION_DRAWER.DELETED, this.deleteItem)
    eventHub.$off(HUB_EVENTS.ENTITY_DRAWER.UPDATED, this.refreshWidget)
    eventHub.$off(HUB_EVENTS.TASK_EDITION_DRAWER.COMPLETED, this.refreshWidget)
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId', 'siteId', 'organization']),

    showEmptyState() {
      return (
        !this.loading && !this.loadingTotalItems && !this.activeEntities.length
      )
    },

    activeEntities() {
      return _.filter(this.totals, item => {
        return item.value
      })
    },

    requestParams() {
      return {
        rev: {
          filters: {
            status: [
              this.$constants.exploreEntityStatus.open,
              this.$constants.exploreEntityStatus.inProgress,
            ],
            types: [
              this.$constants.exploreEntityTypes.improvementIdea,
              this.$constants.exploreEntityTypes.quickAction,
              this.$constants.exploreEntityTypes.problemSolving,
            ],
            assignees: [this.accountId],
          },
          sort: { by: 'updatedAt', order: 'desc' },
        },
        improvementIdea: {
          filters: {
            status: [
              this.$constants.exploreEntityStatus.inProgress,
              this.$constants.exploreEntityStatus.open,
            ],
            types: [this.$constants.exploreEntityTypes.improvementIdea],
            assignees: [this.accountId],
          },
          sort: { by: 'updatedAt', order: 'desc' },
        },
        quickAction: {
          filters: {
            status: [
              this.$constants.exploreEntityStatus.inProgress,
              this.$constants.exploreEntityStatus.open,
            ],
            types: [this.$constants.exploreEntityTypes.quickAction],
            assignees: [this.accountId],
          },
          sort: { by: 'updatedAt', order: 'desc' },
        },
        problemSolving: {
          filters: {
            status: [
              this.$constants.exploreEntityStatus.inProgress,
              this.$constants.exploreEntityStatus.open,
            ],
            types: [this.$constants.exploreEntityTypes.problemSolving],
            assignees: [this.accountId],
          },
          sort: { by: 'updatedAt', order: 'desc' },
        },
        routine: {
          filters: {
            status: [
              this.$constants.exploreEntityStatus.inProgress,
              this.$constants.exploreEntityStatus.open,
            ],
            types: [this.$constants.exploreEntityTypes.routine],
            createdBy: this.accountId,
          },
          sort: { by: 'updatedAt', order: 'desc' },
        },
        finding: {
          filters: {
            status: [
              this.$constants.exploreEntityStatus.inProgress,
              this.$constants.exploreEntityStatus.open,
            ],
            types: [this.$constants.exploreEntityTypes.finding],
            assignees: [this.accountId],
          },
          sort: { by: 'priorityScore', order: 'desc' },
        },
        task: {
          filters: {
            status: [
              this.$constants.exploreEntityStatus.inProgress,
              this.$constants.exploreEntityStatus.open,
            ],
            types: [this.$constants.exploreEntityTypes.task],
            assignees: [this.accountId],
          },
          sort: { by: 'dueDate', order: 'asc' },
        },
      }
    },

    defaultRequestParams() {
      return {
        filters: {
          status: [
            this.$constants.exploreEntityStatus.inProgress,
            this.$constants.exploreEntityStatus.open,
          ],
          types: _.compact([
            this.$constants.exploreEntityTypes.improvementIdea,
            this.$constants.exploreEntityTypes.quickAction,
            this.hasRoutinesFeatureEnabled &&
              this.$constants.exploreEntityTypes.routine,
            this.hasRoutinesFeatureEnabled &&
              this.$constants.exploreEntityTypes.finding,
            this.$constants.exploreEntityTypes.problemSolving,
          ]),
          assignees: [this.accountId],
        },
        sort: {
          by: 'updatedAt',
          order: 'desc',
        },
      }
    },

    hasRoutinesFeatureEnabled() {
      return Boolean(this.organization?.featuresEnabled__routines)
    },

    hasTaskEnabled() {
      return Boolean(this.organization?.featuresEnabled__tasks)
    },

    exploreToDoRoute() {
      return {
        path: '/explore',
        query: {
          assignees: [this.accountId],
          status: [
            this.$constants.exploreEntityStatus.open,
            this.$constants.exploreEntityStatus.inProgress,
          ],
        },
      }
    },

    exploreToDoRouteByEntity() {
      const sort =
        this.activeEntity === 'finding'
          ? 'priorityScore-desc'
          : 'updatedAt-desc'

      if (this.activeEntity === 'task')
        return {
          name: 'tasks',
        }
      return {
        name: 'explore',
        query: {
          ...this.requestParams[this.activeEntity]?.filters,
          sites: [],
          sort,
        },
      }
    },
  },

  methods: {
    hydrateFromSnapshot() {
      const snapshot = this.$store.getters['homeSnapshots/validSnapshot']('toDo')

      if (!snapshot) return

      if (!_.isArray(snapshot.totals) || !_.isArray(snapshot.toDoItems)) return

      this.totals = snapshot.totals.map(entry => ({ ...entry }))
      this.activeEntity = snapshot.activeEntity ?? null
      this.toDoItems = [...snapshot.toDoItems]
      this.itemPages = _.chunk(this.toDoItems, 2)
      this.isTotalsComplete = true
      this.loading = false
      this.loadingTotalItems = false
      this.isShowingCachedSnapshot = true
    },

    async loadToDoData() {
      if (this.isWidgetRoundInFlight) {
        this.isRefreshQueued = true

        return
      }

      this.isWidgetRoundInFlight = true

      try {
        await this.fetchTotalItems()
        await this.fetchRevBreakdown()

        const totalActiveEntity = this.totals.find(
          entity => entity.id === this.activeEntity
        )

        if (!totalActiveEntity?.value) {
          const activeEntity = this.totals.find(entity => entity.value > 0)
          this.setActiveEntity(activeEntity?.id)
        } else {
          this.setActiveEntity(this.activeEntity)
        }
      } finally {
        this.isWidgetRoundInFlight = false
        this.isShowingCachedSnapshot = false

        await this.runQueuedRefresh()
      }
    },

    async fetchRevBreakdown() {
      if (!this.isRevBreakdownPending) return

      this.isRevBreakdownPending = false

      const { task, routine, finding } = this.pendingAuxTotals

      try {
        const [totalImprovementIdeas, totalQuickActions, totalProblemSolvings] = await Promise.all([
          this.fetchTodoItemsCount(this.requestParams.improvementIdea),
          this.fetchTodoItemsCount(this.requestParams.quickAction),
          this.fetchTodoItemsCount(this.requestParams.problemSolving),
        ])

        const breakdownCounts = [
          totalImprovementIdeas,
          totalQuickActions,
          totalProblemSolvings,
        ]

        if (!breakdownCounts.every(_.isNumber)) return

        this.totals = buildTotals({
          totalImprovementIdeas,
          totalQuickActions,
          totalProblemSolvings,
          totalTasks: task,
          totalRoutines: routine,
          totalFindings: finding,
        })

        this.isTotalsComplete = true

        this.saveSnapshot()
      } catch (err) {
        this.handleRequestError(err)

        if (!this.isShowingCachedSnapshot) {
          this.totals = buildTotals({
            totalTasks: task,
            totalRoutines: routine,
            totalFindings: finding,
          })
        }
      } finally {
        this.loadingTotalItems = false
      }
    },

    saveSnapshot() {
      if (!this.isTotalsComplete) return

      this.$store.commit('homeSnapshots/SET_TO_DO_SNAPSHOT', {
        organizationId: this.organizationId,
        accountId: this.accountId,
        totals: this.totals.map(entry => ({ ...entry })),
        activeEntity: this.activeEntity,
        toDoItems: [...this.toDoItems],
      })
    },

    deleteItem() {
      const activeTotal = _.find(this.totals, { id: this.activeEntity })

      if (!activeTotal) return

      const newValue = activeTotal.value - 1

      this.totals = this.totals.map(entry =>
        entry.id === this.activeEntity ? { ...entry, value: newValue } : entry
      )

      this.saveSnapshot()

      if (newValue === 0) {
        this.refreshWidget()
      } else {
        _.delay(this.fetchToDoItems, 500, this.requestParams[this.activeEntity])
      }
    },

    setActiveEntity(newEntity) {
      this.trackHomeNavigate({
        section: 'toDo',
        element: `${newEntity}Counter`,
      })
      this.activeEntity = newEntity
      this.fetchToDoItems(this.requestParams[this.activeEntity])
    },

    async fetchToDoItems(options = {}) {
      try {
        if (!this.isShowingCachedSnapshot) this.loading = true

        const response = await api.explore.getContent({
          organizationId: this.organizationId,
          ...this.defaultRequestParams,
          ...options,
          limit: 10,
          expansions: DEFAULT_EXPANSIONS,
        })

        this.toDoItems =
          response?.data?.results?.map?.(item => ({
            ...item.response,
            id: item.response.id ?? item.response._id,
            dueDate: item.response.dueDate || undefined,
          })) ?? []

        this.itemPages = _.chunk(this.toDoItems, 2)

        this.saveSnapshot()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async fetchTodoItemsCount(options = {}) {
      try {
        if (!this.isShowingCachedSnapshot) this.loading = true

        const response = await api.explore.getContent({
          organizationId: this.organizationId,
          ...this.defaultRequestParams,
          ...options,
          limit: 1,
          projections: [],
          expansions: [],
        })

        return response?.data?.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async fetchTotalItems() {
      if (!this.isShowingCachedSnapshot) this.loadingTotalItems = true

      try {
        const [totalRoutines, totalFindings, totalTasks] = await Promise.all([
          this.hasRoutinesFeatureEnabled
            ? this.fetchTodoItemsCount(this.requestParams.routine)
            : Promise.resolve(0),
          this.hasRoutinesFeatureEnabled
            ? this.fetchTodoItemsCount(this.requestParams.finding)
            : Promise.resolve(0),
          this.hasTaskEnabled
            ? this.fetchTodoItemsCount(this.requestParams.task)
            : Promise.resolve(0),
        ])

        const totalCountersGreaterThanZero = [
          totalRoutines,
          totalFindings,
          totalTasks,
        ].filter(counter => counter > 0).length

        const areAuxCountersValid = [totalRoutines, totalFindings, totalTasks].every(
          _.isNumber
        )

        if (!areAuxCountersValid) return

        // We have room to display up to 4 different counters. If ONLY ONE of the previous counter has a value to display, we should breakdown Revs counters by type to fill the space. Otherwise, we should display the total of all Revs.
        const shouldCountAllRevsAtOnce = totalCountersGreaterThanZero > 1

        if (shouldCountAllRevsAtOnce) {
          const totalRevs = await this.fetchTodoItemsCount(
            this.requestParams.rev
          )

          if (!_.isNumber(totalRevs)) return

          this.totals = buildTotals({
            totalRevs,
            totalTasks,
            totalRoutines,
            totalFindings,
          })

          this.isTotalsComplete = true
        } else {
          this.isRevBreakdownPending = true
          this.isTotalsComplete = false
          this.pendingAuxTotals = {
            task: totalTasks,
            routine: totalRoutines,
            finding: totalFindings,
          }
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        if (!this.isRevBreakdownPending) this.loadingTotalItems = false
      }
    },

    handleShowFinding(finding) {
      this.selectedFindingId = finding.id
      this.isFindingDrawerOpen = true
    },

    handleFindingDrawerClose() {
      this.isFindingDrawerOpen = false
      this.selectedFindingId = null
    },

    async refreshWidget() {
      clearTimeout(this.deferredLoadTimer)

      if (this.isWidgetRoundInFlight) {
        this.isRefreshQueued = true

        return
      }

      this.isWidgetRoundInFlight = true

      try {
        await this.fetchTotalItems()
        await this.fetchRevBreakdown()

        const totalActiveEntity = this.totals.find(
          entity => entity.id === this.activeEntity
        )

        if (!totalActiveEntity?.value) {
          const activeEntity = this.totals.find(entity => entity.value > 0)
          this.setActiveEntity(activeEntity?.id)
        } else {
          this.setActiveEntity(this.activeEntity)
        }
      } finally {
        this.isWidgetRoundInFlight = false
        this.isShowingCachedSnapshot = false

        await this.runQueuedRefresh()
      }
    },

    async runQueuedRefresh() {
      if (!this.isRefreshQueued) return

      this.isRefreshQueued = false

      await this.refreshWidget()
    },

    handleFindingDeleted() {
      this.refreshWidget()
      this.handleFindingDrawerClose()
    },
  },
}
</script>
<style lang="scss">
.todo-item {
  flex: 1 1 0;
  width: 0;
}
</style>
